import { Box, Collapse, Divider, Flex, Icon, Spinner, Text, useToast } from '@chakra-ui/react';
import { saveAs } from 'file-saver';
import { useEffect, useRef, useState } from 'react';
import { FaChevronRight } from 'react-icons/fa';

import {
  DealMediaTypeEnum,
  DocumentProgressStatus,
  ExternalDeal,
  useMediaFromComLazyQuery,
} from '../../../../gql/generated/graphql';
import { createErrorToast } from '../../../../utils/toast';
import VerifyAccountModal from '../../../auth/components/VerifyAccountModal';
import DocListItem from './DocListItem';

interface Props {
  documentsSections: { sectionOneOpen: boolean; sectionTwoOpen: boolean };
  setDocumentsSections: (sections: { sectionOneOpen: boolean; sectionTwoOpen: boolean }) => void;
  deal?: ExternalDeal;
}

const LeaseEndDocs = ({ documentsSections, setDocumentsSections, deal }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canDownload, setCanDownload] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const toast = useToast();
  const [ssnVerificationIsOpen, setSsnVerificationIsOpen] = useState<boolean>(false);

  // This is done with lazyQuery and useEffect so the query re-runs when changing deal in dashboard
  const [fetchMediaFromCom] = useMediaFromComLazyQuery({
    fetchPolicy: 'network-only',
  });

  const getSignedDocsKey = async () => {
    if (!deal?.id) {
      return null;
    }
    const response = await fetchMediaFromCom({ variables: { dealId: deal.id } });
    const signedDocs = response?.data?.mediaFromCom
      ?.filter((doc) => doc.type === DealMediaTypeEnum.SignedDocs && !doc.key?.includes('__LEGACY'))
      ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return signedDocs?.[0]?.signed_url ?? null;
  };

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkHasSignedDocs = async (retry = 0) => {
    if (deal?.document_progress_status !== DocumentProgressStatus.Signed) {
      setCanDownload(false);
      setIsLoading(false);
      return;
    }

    setCanDownload(false);
    setIsLoading(true);

    const signedDocsKey = await getSignedDocsKey();

    // Signed docs really should exist in this state, but it can take a few seconds to be available, so we retry with a delay
    if (!signedDocsKey && retry < 20) {
      timeoutRef.current = setTimeout(() => {
        checkHasSignedDocs(retry + 1);
      }, 1000);
      return;
    }

    setCanDownload(!!signedDocsKey);
    setIsLoading(false);
  };

  useEffect(() => {
    checkHasSignedDocs();
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [deal?.id, deal?.document_progress_status, deal?.state]);

  const showErrorToast = () => {
    toast(
      createErrorToast({
        title: 'Unable to download documents',
        description: 'An error occurred while downloading your documents.',
        errorMessage: 'Failed to download customer documents.',
      }),
    );
  };

  const handleDownload = async () => {
    if (!deal?.id || !canDownload) {
      showErrorToast();
      return;
    }
    setDownloading(true);

    try {
      const url = await getSignedDocsKey(); // get fresh key in case the previous one expired

      const fileName = `SIGNED_DOCS_${deal?.customer?.last_name?.toUpperCase()}.pdf`;

      saveAs(url ?? '', fileName);
    } catch (e) {
      showErrorToast();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box py={4}>
      <Flex
        as="button"
        alignItems="center"
        w="full"
        onClick={() =>
          setDocumentsSections({
            ...documentsSections,
            sectionOneOpen: !documentsSections.sectionOneOpen,
          })
        }
      >
        <Icon
          transform={documentsSections.sectionOneOpen ? 'rotate(90deg)' : 'rotate(0deg)'}
          transition="transform 0.3s ease"
          as={FaChevronRight}
          mr="2"
        />
        <Text fontWeight="semibold">
          LEASE END DOWNLOADS ({isLoading ? <Spinner size="xs" /> : canDownload ? 1 : 0})
        </Text>
      </Flex>
      <Collapse in={documentsSections.sectionOneOpen} animateOpacity>
        <Flex pt={2} px={4} flexDir="column">
          {isLoading ? (
            <Spinner size="sm" />
          ) : canDownload ? (
            <DocListItem
              areLeaseEndDocs
              onDownloadClick={() => setSsnVerificationIsOpen(true)}
              downloading={downloading}
            />
          ) : (
            'No downloads at this time'
          )}
        </Flex>
      </Collapse>
      <Divider borderColor="grayDarkBackground" pt={4} />
      <VerifyAccountModal
        open={ssnVerificationIsOpen}
        onClose={() => setSsnVerificationIsOpen(false)}
        deal={deal}
        download
        handleNextSubmit={handleDownload}
      />
    </Box>
  );
};

export default LeaseEndDocs;
